import { IMillstoneDAIParams } from 'library/ts/interfaces/metadata/millstone/IMillstoneDAIParams';
import { SizeVO } from 'library/ts/model/vo/SizeVO';
import { PlayerAdapterErrorCode } from 'library/ts/player/adapter/enums/PlayerAdapterErrorCode';
import { PlayerAdapterEvent } from 'library/ts/player/adapter/events/PlayerAdapterEvent';
import { IPlayerAdapterErrorInfo } from 'library/ts/player/adapter/interfaces/errors/IPlayerAdapterErrorInfo';
import { IPlayerAdapterErrorEventDetail } from 'library/ts/player/adapter/interfaces/events/detail/IPlayerAdapterErrorEventDetail';
import { IPlayerAdapterEventVO } from 'library/ts/player/adapter/interfaces/events/IPlayerAdapterEventVO';
import { IResourceConfig } from 'library/ts/player/adapter/interfaces/resource/config/IResourceConfig';
import { BackgroundImage } from 'library/ts/ui/components/background/BackgroundImage';
import { CSSUnit } from 'library/ts/ui/enums/css/CSSUnit';
import { CSSValue } from 'library/ts/ui/enums/css/CSSValue';
import { AbstractTagClass } from 'tag/ts/tag/abstract/AbstractTagClass';
import { DaiFailoverCallback } from 'tag/ts/tag/error/DaiFailoverCallback';
import { DaiFailoverOption } from 'tag/ts/tag/error/DaiFailoverOption';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { IErrorManager } from 'tag/ts/tag/interfaces/error/IErrorManager';
import { IFatalTagErrorMsg } from 'tag/ts/tag/interfaces/error/IFatalTagErrorMsg';
import { ITagEventDataVO } from 'tag/ts/tag/interfaces/events/ITagEventDataVO';
import { ITagEventVO } from 'tag/ts/tag/interfaces/events/ITagEventVO';
import { IParamsVO } from 'tag/ts/tag/interfaces/params/IParamsVO';
import { TagLoggerString } from 'tag/ts/tag/model/enums/TagLoggerString';
import { ErrorMessage, urlToken } from 'tag/ts/tag/model/error/ErrorMessage';
import { errorCodeToken } from 'tag/ts/tag/model/error/ErrorTemplate';
import { createErrorUrl } from 'tag/ts/tag/model/error/ErrorUrl';
import { SkinMode } from 'tag/ts/tag/model/type/SkinMode';
import { TagDomAttribute } from 'tag/ts/tag/model/type/TagDomAttribute';
import { ViewLibrary } from 'tag/ts/tag/view/ViewLibrary';

/**
 * @copyright © Paramount Streaming
 * @author Stan Gletski
 * @description Smart Tag error management and recovery system for fatal error handling and DAI failover strategies
 *
 * **Error Management Orchestration:**
 * ErrorManager serves as Smart Tag's comprehensive error handling and recovery system, managing fatal
 * error scenarios, DAI (Dynamic Ad Insertion) failover strategies, and user-facing error display.
 * Ensures video playback resilience through intelligent failover mechanisms and graceful error presentation.
 *
 * **Core Responsibilities:**
 * - **Fatal Error Handling**: Classifies and processes fatal vs. non-fatal player errors
 * - **DAI Failover Strategies**: Multi-tier fallback options for DAI stream failures
 * - **Error UI Management**: Animated error panel overlay with thumbnail backgrounds
 * - **Network Status Handling**: Online/offline state management and appropriate error messaging
 * - **Error Message Generation**: Dynamic error message creation with URL formatting and error codes
 * - **Font Size Optimization**: Responsive font scaling for error displays across device sizes
 *
 * **DAI Failover Strategy:**
 * Implements a 4-tier failover system for DAI stream failures:
 * 1. **Millstone + DAI Params Removed**: Retry with Millstone URL, remove DAI parameters from SSAI
 * 2. **Millstone + SSAI Removed**: Retry with Millstone URL, completely remove SSAI configuration
 * 3. **API Metadata + DAI Params Removed**: Retry with API metadata URL, remove DAI parameters from SSAI
 * 4. **API Metadata + SSAI Removed**: Retry with API metadata URL, completely remove SSAI configuration
 *
 * **Error Classification:**
 * - **DAI Errors**: `DAI_DATA_ERROR`, `DAI_NETWORK_ERROR`, `DAI_SDK_MISSING_ERROR` → Trigger failover
 * - **Fatal Errors**: All other fatal player errors → Direct error display without failover attempts
 * - **Network Errors**: `OFFLINE` state → Temporary error overlay, auto-hide on `ONLINE`
 *
 * **UI Error Display:**
 * - Animated error panel with fade-in effects and thumbnail backgrounds
 * - Responsive font scaling based on container dimensions (1170x658 reference → 10-19px range)
 * - Accessibility support with `role="alert"` for screen readers
 * - Smart message comparison to prevent UI flickering on repeated errors
 *
 * **Business Impact:**
 * Critical for user experience during stream failures, maximizing successful video playback through
 * intelligent failover while providing professional error presentation when recovery fails.
 */
export class ErrorManager extends AbstractTagClass implements IErrorManager {
	#overlayContent: HTMLElement;

	// flag for checking if we have a fatal error in progress
	#fatalError: boolean = false;

	// true once a network-fatal-while-offline has been dispatched to the host for the current
	// offline window — reset on ON_ONLINE so the next outage starts fresh (PLBCK-8332)
	#networkFatalDispatchedThisOfflineWindow: boolean = false;

	// placeholder for DAI failover resource
	#daiFailoverResource: IResourceConfig;

	/**
	 * @description Requests to load video using mediaUrl from millstone and removes millstone DAI parameters from SSAI adParameters
	 */
	readonly #reloadMillstoneWithDaiParamsRemoved: DaiFailoverCallback = (): void => {
		// pull current resource from player manager
		this.#daiFailoverResource = this?.tag?.controller?.playerManager?.currentResource;
		// clone current parameters
		const paramsVO: IParamsVO = this.utils?.Object?.clone( this.params ) as IParamsVO;
		// remove millstone daiParams
		this.#daiFailoverResource = this.#removeMillstoneDaiParamsFromSsai( this.#daiFailoverResource, this.tag?.millstoneUtilities?.getDaiParams() );
		// set mediaUrl to millstone url
		this.#daiFailoverResource.location.mediaUrl = this.tag?.millstoneUtilities?.getStreamingUrl( paramsVO );
		// assign altered resource config to params
		paramsVO.CONTENT.resource = this.#daiFailoverResource;
		this.logger?.log(
			'reloadMillstoneWithDaiParamsRemoved:',
			'set daiFailoverResource to currentResource, removed DAI params from SSAI params, set mediaUrl to millstone url, loading paramsVO...',
			'\nparamsVO:',
			paramsVO,
			'\nparamsVO.CONTENT.resource.location.mediaUrl:',
			paramsVO.CONTENT.resource.location.mediaUrl,
			'\nparamsVO.CONTENT.resource.ad.ssai:',
			paramsVO.CONTENT.resource.ad.ssai
		);
		// load failover resource
		this.#delayedLoad( paramsVO );
	};

	/**
	 * @description Requests to load video using mediaUrl from millstone and removes SSAI parameters
	 */
	readonly #reloadMillstoneWithSsaiRemoved: DaiFailoverCallback = (): void => {
		// pull current resource from player manager
		this.#daiFailoverResource = this?.tag?.controller?.playerManager?.currentResource;
		// clone current parameters
		const paramsVO: IParamsVO = this.utils?.Object?.clone( this.params ) as IParamsVO;
		// remove SSAI parameters
		this.#daiFailoverResource.ad.ssai = null;
		// set mediaUrl to millstone url
		this.#daiFailoverResource.location.mediaUrl = this.tag?.millstoneUtilities?.getStreamingUrl( paramsVO );
		// assign altered resource config to params
		paramsVO.CONTENT.resource = this.#daiFailoverResource;
		this.logger?.log(
			'reloadMillstoneWithSsaiRemoved:',
			'set daiFailoverResource to currentResource, removed SSAI params, set mediaUrl to millstone url, loading paramsVO...',
			'\nparamsVO:',
			paramsVO,
			'\nparamsVO.CONTENT.resource.location.mediaUrl:',
			paramsVO.CONTENT.resource.location.mediaUrl,
			'\nparamsVO.CONTENT.resource.ad.ssai:',
			paramsVO.CONTENT.resource.ad.ssai
		);
		// load failover resource
		this.#delayedLoad( paramsVO );
	};

	/**
	 * @description Requests to load video using mediaUrl from apiMetadata and removes millstone DAI parameters from SSAI adParameters
	 */
	readonly #reloadApiMetadataWithDaiParamsRemoved: DaiFailoverCallback = (): void => {
		// pull current resource from player manager
		this.#daiFailoverResource = this?.tag?.controller?.playerManager?.currentResource;
		// clone current parameters
		const paramsVO: IParamsVO = this.utils?.Object?.clone( this.params ) as IParamsVO;
		// remove millstone daiParams
		this.#daiFailoverResource = this.#removeMillstoneDaiParamsFromSsai( this.#daiFailoverResource, this.tag?.millstoneUtilities?.getDaiParams() );
		// set mediaUrl to APIMetadata url
		this.#daiFailoverResource.location.mediaUrl = this?.apiMetadata?.streamingUrl;
		// assign altered resource config to params
		paramsVO.CONTENT.resource = this.#daiFailoverResource;
		this.logger?.log(
			'reloadApiMetadataWithDaiParamsRemoved:',
			'set daiFailoverResource to currentResource, removed DAI params from SSAI params, set mediaUrl to ApiMetadata url, loading paramsVO...',
			'\nparamsVO:',
			paramsVO,
			'\nparamsVO.CONTENT.resource.location.mediaUrl:',
			paramsVO.CONTENT.resource.location.mediaUrl,
			'\nparamsVO.CONTENT.resource.ad.ssai:',
			paramsVO.CONTENT.resource.ad.ssai
		);
		// load failover resource
		this.#delayedLoad( paramsVO );
	};

	/**
	 * @description Requests to load video using mediaUrl from apiMetadata and removes SSAI parameters
	 */
	readonly #reloadApiMetadataWithSsaiRemoved: DaiFailoverCallback = (): void => {
		// pull current resource from player manager
		this.#daiFailoverResource = this?.tag?.controller?.playerManager?.currentResource;
		// clone current parameters
		const paramsVO: IParamsVO = this.utils?.Object?.clone( this.params ) as IParamsVO;
		// remove SSAI parameters
		this.#daiFailoverResource.ad.ssai = null;
		// set mediaUrl to APIMetadata url
		this.#daiFailoverResource.location.mediaUrl = this?.apiMetadata?.streamingUrl;
		// assign altered resource config to params
		paramsVO.CONTENT.resource = this.#daiFailoverResource;
		this.logger?.log(
			'reloadApiMetadataWithSsaiRemoved:',
			'set daiFailoverResource to currentResource, removed SSAI params, set mediaUrl to ApiMetadata url, loading paramsVO...',
			'\nparamsVO:',
			paramsVO,
			'\nparamsVO.CONTENT.resource.location.mediaUrl:',
			paramsVO.CONTENT.resource.location.mediaUrl,
			'\nparamsVO.CONTENT.resource.ad.ssai:',
			paramsVO.CONTENT.resource.ad.ssai
		);
		// load failover resource
		this.#delayedLoad( paramsVO );
	};

	/**
	 * @description Options to execute on DAI error. Each option should be attempted in insert-order and flagged accordingly
	 */
	readonly daiFailoverOptions: DaiFailoverOption[] = [
		{
			callback: this.#reloadMillstoneWithDaiParamsRemoved,
			attempted: false
		},
		{
			callback: this.#reloadMillstoneWithSsaiRemoved,
			attempted: false
		},
		{
			callback: this.#reloadApiMetadataWithDaiParamsRemoved,
			attempted: false
		},
		{
			callback: this.#reloadApiMetadataWithSsaiRemoved,
			attempted: false
		}
	];

	/**
	 * @description Initializes ErrorManager with comprehensive error handling and DAI failover capabilities
	 *
	 * **Initialization Strategy:**
	 * Sets up the ErrorManager as a central error handling coordinator for Smart Tag, establishing
	 * event listeners for fatal errors, network state changes, player events, and UI resize events.
	 * Configures DAI failover options and initializes responsive font sizing.
	 *
	 * **Event Handler Registration:**
	 * - **Core Events**: `ON_FATAL_ERROR`, `ON_PLAYER_EVENT`, `ON_API_RESET_TAG_REQUEST`
	 * - **Network Events**: `ON_OFFLINE`, `ON_ONLINE` for network status error handling
	 * - **Lifecycle Events**: `ON_CREATE_PLAYER_SUCCESS`, `ON_REQUEST_TO_CLEAR_ERROR`, `ON_VIEW_READY`
	 * - **Conditional Events**: `ON_RESIZE` (only for chromeless skin with error panel enabled)
	 *
	 * **Resize Event Conditional Logic:**
	 * Only subscribes to resize events when error panel display is enabled and skin is chromeless,
	 * optimizing performance by avoiding unnecessary event handlers.
	 *
	 * **Initialization Sequence:**
	 * 1. Register with AbstractTagClass infrastructure with 'ErrorManager' name
	 * 2. Configure event listeners based on skin and error panel settings
	 * 3. Set up Tag event handling pipeline
	 * 4. Initialize responsive font sizing with delay for DOM readiness
	 *
	 * @param {ITagAbstractClassArgument} argument Standard Smart Tag component initialization argument
	 */
	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'ErrorManager'
			}
		);

		// events we are interested in
		const eventsOfInterest: TagEvent[] = [
			TagEvent.ON_API_RESET_TAG_REQUEST,
			TagEvent.ON_CREATE_PLAYER_SUCCESS,
			TagEvent.ON_FATAL_ERROR,
			TagEvent.ON_OFFLINE,
			TagEvent.ON_ONLINE,
			TagEvent.ON_PLAYER_EVENT,
			TagEvent.ON_REQUEST_TO_CLEAR_ERROR,
			TagEvent.ON_VIEW_READY
		];

		// subscribe to resize event only when needed
		if (
			this?.params?.SKIN?.enabled === true
			&&
			this?.tag?.model?.skinMode === SkinMode.DESKTOP
			&&
			this.params.TAG.errorPanel === true ) {
			eventsOfInterest.push( TagEvent.ON_RESIZE );
		}

		// setup tag events handler
		this.tagEventsHandler( this.#onTagEvent, eventsOfInterest );

		this.utils.Delay.delay().then( () => this.#setFontSize() );
	}

	/**
	 * @description Returns reference to SmartTag container element, the one with attribute smart-tag-overlay='uuid'
	 * @return {HTMLElement}
	 */
	get overlay(): HTMLElement {
		if ( this.utils.Type.isElement( this.tag?.params?.TAG?.container ) ) {
			return ( this.tag.params.TAG.container as HTMLElement ).querySelector(
				'[' + TagDomAttribute.OVERLAY + '=' + this.uuid + ']'
			);
		} else {
			return undefined;
		}
	}

	/**
	 * @description Returns flag indicating if we have a fatal error in progress
	 * @return {boolean}
	 */
	get fatalError(): boolean {
		return this.#fatalError;
	}

	/**
	 * @description Returns Error message string by error code
	 * @param {string | number} code
	 * @return {string}
	 */
	createErrorMessageByCode( code: string | number ): string {
		this?.logger?.log( 'createErrorMessageByCode:', 'code', code );

		// ErrorCopy Tag config module
		const errorCopy = this.tag.model.config.tag.ErrorCopy;
		// ErrorCopy Tag config module params
		const params = errorCopy.params;

		this?.logger?.log( 'createErrorMessageByCode:', 'params', params );

		const msgId: string = params.map[ code ] || params.map.DEFAULT;
		const msgObj = params.message[ msgId ];
		const urlObj = params.url[ msgObj.urlId ];
		let msgCopy = msgObj.copy;

		if ( this.utils.Type.isNullOrUndefined( urlObj ) === false ) {
			const urlMsg = createErrorUrl( urlObj.copy, urlObj.url, urlObj.target, params.style.url.color );
			msgCopy = msgCopy.replace( urlToken, urlMsg );
		}

		if ( msgObj.addError === true ) {
			let errorTemplate = params.templates?.error;
			errorTemplate = errorTemplate.replace( errorCodeToken, code.toString() );
			msgCopy += errorTemplate;
		}
		this?.logger?.log( 'createErrorMessageByCode:', 'msgCopy', msgCopy );
		return msgCopy;
	}

	/**
	 * @description Sets font size on Tag skeleton overlay container
	 */
	#setFontSize = (): void => {
		// sanity check for undefined overlay element
		if ( this.utils.Type.isElement( this.overlay ) === false ) {
			return;
		}

		const overlayDomRect = this.overlay.getBoundingClientRect();

		const newFontSize = this.utils.Number.roundToDecimal(
			this.utils.Calc.dynamicValue(
				new SizeVO( 1170, 658 ),
				new SizeVO( overlayDomRect.width, overlayDomRect.height ),
				16,
				10,
				19
			),
			2
		);

		// set font size on tag overlay container element
		this.overlay.style.setProperty( 'font-size', ( newFontSize + CSSUnit.PIXEL ), CSSValue.IMPORTANT );
	};

	/**
	 * @description Shows error panel overlay
	 */
	#showErrorPanelOverlay = ( message: string ): void => {
		// sanity check for undefined overlay element
		if ( this.utils.Type.isElement( this.overlay ) === false ) {
			return;
		}

		// If currently displayed message is already displayed in active overlay =>
		// exit skipping recreation of Error message preventing UI flickering
		if ( this.overlay.innerHTML === message ) {
			return;
		}

		this.overlay.innerHTML = '';

		// Always hide loading animation when showing error (whether error panel is shown or not)
		if ( this.utils.Type.isFunction( this?.tag?.view?.loadingAnimationManager?.hide ) ) {
			this.tag.view.loadingAnimationManager.hide();
		}

		// prevent error panel from display if requested via Smart Tag params
		if ( this.params.TAG.errorPanel === false ) {
			return;
		}

		this.#overlayContent = this.utils.DOM.stringToElement( ViewLibrary.ERROR_PANEL );
		this.overlay.appendChild( this.#overlayContent );

		const errorContentEl = this.#overlayContent.querySelector( '.smart-tag-error-panel-content' );
		errorContentEl.innerHTML = message;

		if ( this.utils.URL.valid( this?.tag?.model?.apiMetadata?.thumbnail ) ) {
			new BackgroundImage(
				this.#overlayContent.querySelector(
					'.background-image'
				) as HTMLElement,
				this?.tag?.model?.apiMetadata.thumbnail,
				undefined,
				undefined,
				undefined,
				this.utils,
				this.runtimeVO
			);
		}

		this.utils.Delay.delay( 100 )
			.then(
				() => {
					if ( !this.utils.Type.isEmpty( this?.overlay?.style ) ) {
						this.overlay.style.setProperty( 'top', '0', CSSValue.IMPORTANT );
					}
				}
			)
			.then( () => this.utils.Delay.delay() )
			.then(
				() => {
					if ( this.utils.Type.isElement( this.#overlayContent ) ) {
						this.#overlayContent.style.setProperty( 'opacity', '1', CSSValue.IMPORTANT ); // fade in
					}
					if ( this.utils.Type.isElement( errorContentEl ) ) {
						errorContentEl.setAttribute( 'role', 'alert' );
					}
				}
			);
	};

	/**
	 * @description Hides error panel overlay
	 */
	#hideErrorPanelOverlay = (): void => {
		// sanity check for undefined overlay element
		if ( this.utils.Type.isElement( this.overlay ) === false ) {
			return;
		}

		this.overlay.innerHTML = '';
		this.#overlayContent = undefined;
		this.overlay.style.setProperty( 'top', '-10000px', CSSValue.IMPORTANT );
	};

	/**
	 * @description Returns whether a repeat network-fatal-while-offline dispatch should be
	 * suppressed (PLBCK-8332). The first occurrence in an offline window still needs to reach
	 * the host (e.g. so TN Web's `wasFatalErrorRef`-style state gets set for the reconnect
	 * reload) — only repeats of the same ongoing outage before the next `ON_ONLINE` are noise.
	 * Checked via `code`, not `category` — avia's shared `createError()` always passes `category`
	 * as `undefined` regardless of adapter (confirmed against avia-js@2.62.1, the version Smart
	 * Tag pins), so `category` can never be used for this check on any adapter, Shaka included.
	 * @param {IPlayerAdapterErrorInfo} eventDetail
	 * @returns {boolean}
	 */
	#shouldSuppressRepeatNetworkFatalWhileOffline = ( eventDetail: IPlayerAdapterErrorInfo ): boolean => {
		return this.tag?.tagUtilities?.isNetworkFatalWhileOffline( eventDetail )
			&& this.#networkFatalDispatchedThisOfflineWindow === true;
	};

	/**
	 * @description Dispatches Smart Tag fatal error event with processed error details and custom error messaging
	 *
	 * **Fatal Error Event Processing:**
	 * Transforms player adapter error events into Smart Tag's internal fatal error format,
	 * enriching the error with custom messaging and proper event structure for consumption
	 * by other Smart Tag components and external integrations.
	 *
	 * **Error Processing Pipeline:**
	 * - Extracts error details from player adapter event
	 * - Skips dispatch entirely for a repeat network-category fatal within the same known-offline
	 *   window (PLBCK-8332, see `#shouldSuppressRepeatNetworkFatalWhileOffline`) — the first
	 *   occurrence in a window still dispatches normally so hosts are notified once
	 * - Logs error details with appropriate warning level
	 * - Generates custom error message using error code mapping
	 * - Dispatches `TagEvent.ON_FATAL_ERROR` with enriched error data
	 *
	 * **Error Data Structure:**
	 * - **`code`**: Player adapter error code for programmatic handling
	 * - **`msg.original`**: Raw error message from player adapter
	 * - **`msg.custom`**: User-friendly formatted error message with URLs and styling
	 * - **`error`**: Complete original player adapter event for debugging
	 * - **`networkContext.offline`**: Whether this fatal is network-caused during a known outage
	 *   (PLBCK-8332) — lets hosts opt in to distinguishing offline-caused fatals from genuine ones
	 *
	 * **Integration Impact:**
	 * Triggers Smart Tag's fatal error handling flow, including error UI display,
	 * external API notifications, and graceful degradation procedures.
	 *
	 * @param {IPlayerAdapterEventVO} playerAdapterEvent Player adapter error event to process and dispatch
	 */
	#dispatchFatalErrorEvent = ( playerAdapterEvent: IPlayerAdapterEventVO ): void => {
		const eventDetail = ( playerAdapterEvent?.detail as IPlayerAdapterErrorEventDetail )?.error;

		// dedupe repeated host notifications for one ongoing offline-caused network outage (PLBCK-8332)
		if ( this.#shouldSuppressRepeatNetworkFatalWhileOffline( eventDetail ) ) {
			this?.logger?.log( 'dispatchFatalErrorEvent:', 'suppressing repeat network-fatal-while-offline dispatch', eventDetail );
			return;
		}

		if ( this.tag?.tagUtilities?.isNetworkFatalWhileOffline( eventDetail ) ) {
			this.#networkFatalDispatchedThisOfflineWindow = true;
		}

		this?.logger?.warn( TagLoggerString.ON_PLAYER_EVENT, playerAdapterEvent?.type, playerAdapterEvent, eventDetail?.message );
		// tag fatal error event dispatched internally
		this.dispatchTagEvent(
			this.tag.getTagEventVO(
				TagEvent.ON_FATAL_ERROR,
				this.tag,
				{
					fatalError: {
						code: eventDetail?.code,
						msg: {
							original: eventDetail?.message,
							custom: this.createErrorMessageByCode( eventDetail?.code )
						},
						error: playerAdapterEvent,
						// lets hosts opt in to distinguishing offline-caused fatals (PLBCK-8332) —
						// same predicate as the internal panel gate, not bare connectivity, so a
						// DRM/decode fatal that happens to land during an outage isn't misreported
						// as offline-caused
						networkContext: {
							offline: this.tag?.tagUtilities?.isNetworkFatalWhileOffline( eventDetail )
						}
					}
				}
			)
		);
	};

	/**
	 * @description Determines whether DAI failover strategies should be attempted based on configuration and embed type
	 *
	 * **DAI Failover Requirements Assessment:**
	 * Evaluates system configuration to determine if DAI failover options are available and appropriate
	 * for the current playback context. This prevents unnecessary failover attempts when configuration
	 * doesn't support DAI recovery strategies.
	 *
	 * **Validation Criteria:**
	 * - `embedType` must be defined (Desktop/Mobile Web vs Smart TV/HTML5)
	 * - `daiFailover` configuration must exist and be a valid array
	 * - Failover options must be properly configured for the embed type
	 *
	 * **Business Logic:**
	 * Prevents wasted recovery attempts when DAI failover isn't configured or supported,
	 * ensuring efficient error handling and faster transition to final error state.
	 *
	 * @returns {boolean} True if DAI failover should be attempted, false to skip directly to fatal error
	 */
	#daiFailoverRequired = (): boolean => {
		// return false if missing required data
		if (
			this.utils?.Type.isEmpty( this?.params?.SYSTEM?.embedType ) ||
			this.utils?.Type?.isEmpty( this?.params?.CONTENT?.daiFailover ) ||
			!this.utils.Type.isArray( this?.params?.CONTENT?.daiFailover )
		) {
			this?.logger?.log(
				'daiFailoverRequired:',
				'required params are invalid... returning false',
				'\nparams.SYSTEM:',
				this?.params?.SYSTEM,
				'\nparams.CONTENT.daiFailover:',
				this?.params?.CONTENT?.daiFailover
			);
			return false;
		}

		// return true if current embedType is included in daiFailover array, else return false
		return this?.params?.CONTENT?.daiFailover?.includes( this?.params?.SYSTEM?.embedType );
	};

	/**
	 * @description Executes delayed video reload via Video API with modified parameters for DAI failover scenarios
	 *
	 * **Delayed Reload Strategy:**
	 * Implements a brief delay before attempting video reload to allow the player to properly
	 * clean up from the previous failed attempt. This prevents race conditions and ensures
	 * the player is in a stable state before applying failover configuration.
	 *
	 * **Reload Process:**
	 * - Applies configurable delay (default 50ms) for player state stabilization
	 * - Calls `VideoAPI.load()` with modified parameters containing failover resource config
	 * - Logs success/failure for monitoring and debugging
	 * - Handles both successful recovery and secondary failure scenarios
	 *
	 * **Error Handling:**
	 * If the failover attempt also fails, the error is logged but does not trigger additional
	 * failover attempts - the next tier failover (if available) will be handled by the main
	 * error handling flow.
	 *
	 * @param {IParamsVO} paramsVO Modified Smart Tag parameters with failover resource configuration
	 * @param {number} delay Delay in milliseconds before executing reload (default: 50ms)
	 */
	#delayedLoad = ( paramsVO: IParamsVO, delay: number = 50 ): void => {
		this.logger?.log(
			'delayedLoad:',
			'calling VideoAPI.load(paramsVO) after delay...',
			'\ndelay:',
			delay,
			'\nparamsVO:',
			paramsVO,
			'\nparamsVO.CONTENT.resource:',
			paramsVO?.CONTENT?.resource,
			'\nresource.location.mediaUrl:',
			paramsVO?.CONTENT?.resource?.location?.mediaUrl,
			'\nresource.ad.ssai:',
			JSON.stringify( paramsVO?.CONTENT?.resource?.ad?.ssai, null, 4 )
		);

		// IMPORTANT: delay load allowing to process previous player destruction
		this.utils?.Delay?.delay( delay ).then(
			() => {
				this.tag?.API?.VIDEO?.load( paramsVO )
					.then( ( res ) => {
						this.logger?.log( 'delayedLoad:', 'VideoAPI.load(paramsVO) success', 'res:', res );
					} )
					.catch( ( error ) => {
						this.logger?.warn( 'delayedLoad:', 'VideoAPI.load(paramsVO) ERROR', 'error:', error );
					} );
			}
		);
	};

	/**
	 * @description Returns next DAI failover option to execute from the failover strategy queue
	 *
	 * **Failover Strategy Selection:**
	 * Iterates through the 4-tier DAI failover options in insertion order and returns the first
	 * option that has not yet been attempted. This ensures systematic progression through
	 * failover strategies from most preferred (Millstone + DAI params removed) to least
	 * preferred (API Metadata + SSAI removed).
	 *
	 * **Selection Logic:**
	 * - Returns first `DaiFailoverOption` where `attempted === false`
	 * - Maintains failover strategy progression order
	 * - Returns `undefined` when all strategies have been attempted
	 *
	 * @returns {DaiFailoverOption|undefined} Next unattempted failover option or undefined if all attempted
	 */
	#getNextDaiFailoverOption = (): DaiFailoverOption | undefined => {
		// iterates through daiFallbackOptions in insert-order and returns first one not yet flagged as attempted
		return this.daiFailoverOptions.find(
			( failoverOption: DaiFailoverOption ) => {
				if ( failoverOption.attempted === false ) {
					return failoverOption;
				}
				return undefined;
			} );
	};

	/**
	 * @description Removes Millstone DAI parameters from SSAI adParameters in ResourceConfig for failover scenarios
	 *
	 * **DAI Parameter Removal Strategy:**
	 * During DAI failover scenarios, conflicts can occur between Millstone DAI parameters and SSAI
	 * ad parameters. This method surgically removes Millstone-specific DAI parameters from the
	 * SSAI configuration while preserving other ad parameters, enabling fallback playback.
	 *
	 * **Removal Process:**
	 * - Iterates through each Millstone DAI parameter key
	 * - Removes matching keys from `resource.ad.ssai.adParameters`
	 * - Logs removal details for debugging and monitoring
	 * - Preserves non-conflicting SSAI ad parameters
	 *
	 * **Business Logic:**
	 * Critical for DAI failover success - prevents parameter conflicts that could cause
	 * secondary failures during recovery attempts.
	 *
	 * @param {IResourceConfig} resource The video resource configuration to modify
	 * @param {IMillstoneDAIParams} daiParams Millstone DAI parameters to remove from SSAI config
	 * @returns {IResourceConfig} Modified resource configuration with DAI parameters removed
	 */
	#removeMillstoneDaiParamsFromSsai = ( resource: IResourceConfig, daiParams: IMillstoneDAIParams ): IResourceConfig => {
		if (
			this.utils?.Type?.isObject( daiParams ) &&
			!this.utils?.Type?.isEmpty( daiParams )
		) {
			Object.keys( daiParams ).forEach(
				( key ) => {
					const value = resource?.ad?.ssai?.adParameters?.[ key ];
					delete resource?.ad?.ssai?.adParameters?.[ key ];
					this.logger?.log(
						'removeMillstoneDaiParamsFromSsai:',
						'\nremoved millstone DAI param from SSAI adParameters',
						'\nkey:',
						key,
						'\nvalue:',
						value,
						'\nresource.ad.ssai.adParameters:',
						resource?.ad?.ssai?.adParameters
					);
				}
			);
		}
		return resource;
	};

	/**
	 * @description Executes DAI Failover option and flags to avoid attempting again
	 * @param {DaiFailoverOption} option
	 * @param {IPlayerAdapterEventVO} playerAdapterEvent
	 */
	#executeDaiFailoverOption = ( option: DaiFailoverOption, playerAdapterEvent: IPlayerAdapterEventVO ): void => {
		this.logger?.log( 'executeDaiFailoverOption:', 'executing DAI failover option...', '\noption:', option, '\nevent:', playerAdapterEvent );
		try {
			// flag to prevent 2nd attempt
			option.attempted = true;
			// execute fallback
			option?.callback();
		} catch ( error ) {
			this.logger?.warn( 'executeDaiFailoverOption:', 'error executing DAI failover option', '\noption:', option, '\nerror:', error );
		}
	};

	/**
	 * @description Attempts to retry loading video using failover options, if required
	 * @param {IPlayerAdapterEventVO} playerAdapterEvent
	 */
	#handleDaiError = ( playerAdapterEvent: IPlayerAdapterEventVO ): void => {
		const eventDetail = ( playerAdapterEvent?.detail as IPlayerAdapterErrorEventDetail )?.error;
		this?.logger?.log( 'handleDaiError:', TagLoggerString.ON_PLAYER_EVENT, playerAdapterEvent?.type, 'fatal:', eventDetail?.fatal, 'ERROR CODE:', eventDetail?.code, 'eventDetail:', eventDetail );

		if ( this.#daiFailoverRequired() === false ) {
			this?.logger?.log( 'handleDaiError:', 'DAI Failover is not required... dispatching Fatal Error' );
			this.#dispatchFatalErrorEvent( playerAdapterEvent );
			return;
		}

		// get the next fallback option which has not yet been attempted
		const unattemptedFallbackOption: DaiFailoverOption = this.#getNextDaiFailoverOption();

		// all fallback options attempted, dispatch Fatal Error
		if ( this.utils?.Type?.isEmpty( unattemptedFallbackOption ) ) {
			this?.logger?.log( 'handleDaiError:', 'all fallback options attempted... dispatching Fatal Error' );
			this.#dispatchFatalErrorEvent( playerAdapterEvent );
			return;
		}

		// attempt fallback
		this.#executeDaiFailoverOption( unattemptedFallbackOption, playerAdapterEvent );
	};

	/**
	 * @description Player event handler
	 * @param {ITagEventDataVO} data
	 */
	#onPlayerEvent = ( data: ITagEventDataVO ): void => {
		// player event
		const playerAdapterEvent = data?.playerAdapterEvent;

		// sanity check for empty player event object + IMPORTANT: if our error is indeed fatal
		if ( this.utils.Type.isEmpty( playerAdapterEvent ) || ( playerAdapterEvent?.detail as IPlayerAdapterErrorEventDetail )?.error?.fatal !== true ) {
			return;
		}

		// filter by player event type
		switch ( playerAdapterEvent?.type ) {
			case PlayerAdapterEvent.ERROR:
				const eventDetail = ( playerAdapterEvent?.detail as IPlayerAdapterErrorEventDetail )?.error;

				// filter by error code
				switch ( eventDetail?.code ) {
					// DAI failures...
					case PlayerAdapterErrorCode.DAI_DATA_ERROR:
					case PlayerAdapterErrorCode.DAI_NETWORK_ERROR:
					case PlayerAdapterErrorCode.DAI_SDK_MISSING_ERROR:
						this.#handleDaiError( playerAdapterEvent );
						break;
					default: // for all other events
						// execute regular fatal error event dispatch
						this.#dispatchFatalErrorEvent( playerAdapterEvent );
				}
		}
	};

	/**
	 * @description Tag application event handler
	 * @param {ITagEventVO} event
	 */
	#onTagEvent = ( event: ITagEventVO ): void => {

		switch ( event?.type ) {
			case TagEvent.ON_VIEW_READY:
				// remove view ready event listener
				this.removeTagEventsHandler( this.#onTagEvent, this?.tag?.eventDispatcher, [ event?.type ] ).then();
				this.#setFontSize();
				break;
			case TagEvent.ON_RESIZE:
				this.#setFontSize();
				break;

			case TagEvent.ON_ONLINE:
				this.#networkFatalDispatchedThisOfflineWindow = false;
				this.#hideErrorPanelOverlay();
				break;

			case TagEvent.ON_OFFLINE:
				if ( this.#fatalError === false ) this.#showErrorPanelOverlay( ErrorMessage[ 'OFFLINE' ].copy );
				break;

			case TagEvent.ON_FATAL_ERROR:
				this.#fatalError = true;

				if ( this?.params?.CONTENT?.suppressErrorPanel === true ) {
					this.params.CONTENT.suppressErrorPanel = false;
					this?.logger?.warn( TagLoggerString.ON_FATAL_ERROR, 'suppressErrorPanel set - skipping error panel', event?.data?.fatalError );
					break;
				}

				// a network fatal while already known-offline is redundant with the offline panel
				// already showing — same cause, no new information — so skip the separate fatal-error
				// panel. Host dispatch already happened via #dispatchFatalErrorEvent; only the local
				// visual panel is skipped here. Non-network fatals (DRM, decode, etc.) still show
				// normally even while offline, since those aren't caused by the outage (PLBCK-8332)
				if ( this.tag?.tagUtilities?.isNetworkFatalWhileOffline( { code: event?.data?.fatalError?.code as string } ) ) {
					this?.logger?.log( TagLoggerString.ON_FATAL_ERROR, 'skipping error panel — network fatal while offline panel already showing', event?.data?.fatalError );
					break;
				}

				// process message
				const msg: IFatalTagErrorMsg = event?.data?.fatalError?.msg;
				let msgToShow: string = msg?.custom;
				if ( this.utils.Type.isString( msgToShow ) === false ) msgToShow = msg?.original;

				// if we have defined error message
				if ( this.utils.Type.isString( msgToShow ) ) {
					// replace error code with the one from the actual error object
					msgToShow = msgToShow.replace( errorCodeToken, event?.data?.fatalError?.code as string );
					this.#showErrorPanelOverlay( msgToShow );
				}

				this?.logger?.warn( TagLoggerString.ON_FATAL_ERROR, event?.data?.fatalError, '\nMessage:', msg );
				break;

			case TagEvent.ON_CREATE_PLAYER_SUCCESS:
				// don't hide the offline panel out from under an in-progress outage (PLBCK-8332) —
				// a mid-outage load()/reconnect can recreate the player before the new resource's
				// own network fatal arrives, and #onPlayerEvent's suppression branch assumes this
				// panel is still visible when that fatal lands. Gated behind offlineFatalRecovery
				// (via shouldPreserveOfflinePanel) so a non-opted-in host keeps the pre-PLBCK-8332
				// unconditional hide, unchanged.
				this.utils.Delay.delay().then(
					() => {
						if ( this.tag?.tagUtilities?.shouldPreserveOfflinePanel() === true ) {
							this.#showErrorPanelOverlay( ErrorMessage[ 'OFFLINE' ].copy );
						} else {
							this.#hideErrorPanelOverlay();
						}
					}
				);
				break;

			case TagEvent.ON_REQUEST_TO_CLEAR_ERROR:
			case TagEvent.ON_API_RESET_TAG_REQUEST:
				this?.logger?.log( TagLoggerString.ON_TAG_EVENT, event?.type, event );
				this.#fatalError = false; // clear fatal error flag
				// a fresh load/reset gets its own dedupe window rather than inheriting the
				// previous load's latch — otherwise a network fatal from this new attempt would
				// be silently suppressed with no host notification and no local panel (PLBCK-8332)
				this.#networkFatalDispatchedThisOfflineWindow = false;
				// gated behind offlineFatalRecovery (via shouldPreserveOfflinePanel) so a
				// non-opted-in host keeps the pre-PLBCK-8332 unconditional hide, unchanged
				if ( this.tag?.tagUtilities?.shouldPreserveOfflinePanel() === true ) {
					this.#showErrorPanelOverlay( ErrorMessage[ 'OFFLINE' ].copy );
				} else {
					this.#hideErrorPanelOverlay();
				}
				break;

			case TagEvent.ON_PLAYER_EVENT:
				this.#onPlayerEvent( event?.data );
				break;
		}

	};
}